import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import type { I18n } from '@/lib/api/types';
import { useLocale } from '@/lib/i18n/useLocale';
import { cn } from '@/lib/ui/cn';
import { inputBase } from '@/lib/ui/Field';
import { Switch } from '@/lib/ui/Switch';
import {
  asI18n,
  classify,
  humanizeKey,
  isPlainObject,
  pathToString,
  type Json,
  type NodeKind,
  type Path,
} from './pageCopyTree';

/**
 * Recursive **text editor** for a page-copy content tree. The document's *shape* is fixed —
 * editors can only change the copy in existing leaves, never add, remove, rename, or reorder
 * fields (those are authoring-time concerns handled elsewhere). So this renders the tree as a
 * read-only outline of labelled sections whose `{ en, ar }` leaves expose editable EN + AR
 * fields side by side. Every edit flows through the single {@link TreeOps.update} dispatch the
 * parent supplies, keyed by an absolute {@link Path} from the content root.
 */
export interface TreeOps {
  /** Replace the value at `path`. The only mutation the editor permits. */
  update: (path: Path, value: Json) => void;
}

const TreeContext = createContext<TreeOps | null>(null);
function useTreeOps(): TreeOps {
  const ctx = useContext(TreeContext);
  if (!ctx) throw new Error('PageCopyFields must be rendered inside <TreeProvider>');
  return ctx;
}

export function TreeProvider({ ops, children }: { ops: TreeOps; children: ReactNode }) {
  return <TreeContext.Provider value={ops}>{children}</TreeContext.Provider>;
}

/**
 * Deep-link focus: the global search routes to one field via its `field_path`. The matching
 * row scrolls into view and pulses once so the user lands on *exactly* the item they clicked.
 * `locale` is the locale the search hit matched in — for locale-first content this decides
 * whether we land on the `en.*` or `ar.*` copy; `null` falls back to the active UI locale.
 */
interface FocusTarget {
  path: string | null;
  locale: 'en' | 'ar' | null;
}
const FocusContext = createContext<FocusTarget>({ path: null, locale: null });
export function FocusProvider({
  path,
  locale = null,
  children,
}: {
  path: string | null;
  locale?: 'en' | 'ar' | null;
  children: ReactNode;
}) {
  return <FocusContext.Provider value={{ path, locale }}>{children}</FocusContext.Provider>;
}

/** How long the deep-link pulse stays lit before it fades out. */
const PULSE_MS = 1800;

/**
 * Whether a tree `path` is the field that the search deep-link (`field_path`) points at.
 *
 * Page copy is stored **locale-first** (`{ en: {…}, ar: {…} }`) and the search index strips
 * the leading locale, so `field_path: "headline"` addresses both `en.headline` and
 * `ar.headline`. We match the occurrence in the **active UI locale** (the locale the editor
 * searched in) so exactly one row scrolls + pulses. We also accept an exact match, which
 * covers the bilingual-leaf shape (`{ headline: { en, ar } }`).
 */
export function matchesFieldPath(path: Path, target: string, locale: 'en' | 'ar'): boolean {
  if (pathToString(path) === target) return true;
  const head = path[0];
  if (head !== locale) return false; // only the active-locale copy, so we land once
  return pathToString(path.slice(1)) === target;
}

/**
 * Returns a ref + a transient `highlighted` flag for the row whose path matches the active
 * deep-link target. On a match it scrolls into view and lights up once, then fades — a
 * one-shot pulse rather than a permanent marker.
 */
function useFieldFocus(path: Path) {
  const { path: target, locale: matchedLocale } = useContext(FocusContext);
  const { locale: uiLocale } = useLocale();
  // Prefer the locale the search actually matched; fall back to the UI locale otherwise.
  const locale = matchedLocale ?? uiLocale;
  const isMatch = target != null && matchesFieldPath(path, target, locale);
  const ref = useRef<HTMLDivElement>(null);
  const [highlighted, setHighlighted] = useState(false);
  useEffect(() => {
    if (!isMatch) {
      setHighlighted(false);
      return;
    }
    setHighlighted(true);
    // Defer the scroll a frame so layout is settled before measuring.
    const raf = requestAnimationFrame(() =>
      ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }),
    );
    const timer = setTimeout(() => setHighlighted(false), PULSE_MS);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timer);
    };
  }, [isMatch, target]);
  return { ref, highlighted };
}

/** Shared one-shot focus-pulse ring for a deep-linked row. */
function focusRing(highlighted: boolean): string {
  return cn(
    'transition-[box-shadow,border-color] ease-almanac',
    highlighted
      ? 'border-accent-blue shadow-focus duration-almanac'
      : 'border-hairline duration-[1200ms]',
  );
}

// ── Object (group) ────────────────────────────────────────────────────────────
function GroupFields({ value, path }: { value: Record<string, Json>; path: Path }) {
  const { t } = useTranslation();
  const entries = Object.entries(value);
  if (entries.length === 0) {
    return <p className="text-small text-ink-mute">{t('settings.pageCopy.emptyGroup')}</p>;
  }
  return (
    <div className="flex flex-col gap-sm">
      {entries.map(([key, child]) => (
        <FieldRow key={key} name={key} value={child} path={[...path, key]} />
      ))}
    </div>
  );
}

// ── Array (list) ──────────────────────────────────────────────────────────────
function ListFields({ value, path }: { value: Json[]; path: Path }) {
  const { t } = useTranslation();
  if (value.length === 0) {
    return <p className="text-small text-ink-mute">{t('settings.pageCopy.emptyList')}</p>;
  }
  return (
    <div className="flex flex-col gap-sm">
      {value.map((item, i) => (
        <ItemRow key={i} index={i} value={item} path={[...path, i]} />
      ))}
    </div>
  );
}

/**
 * A labelled field inside an object. Leaves render their editor inline; groups and lists
 * render a section header and recurse. The key is shown as a human-readable label with the
 * raw key kept as a quiet mono caption (it's what search deep-links address).
 */
function FieldRow({ name, value, path }: { name: string; value: Json; path: Path }) {
  const kind = classify(value);
  const { ref, highlighted } = useFieldFocus(path);
  const leaf = kind === 'i18n' || kind === 'text' || kind === 'number' || kind === 'boolean' || kind === 'null';

  if (leaf) {
    return (
      <div ref={ref} className={cn('rounded-md border-[0.5px] bg-canvas-paper p-sm', focusRing(highlighted))}>
        <FieldLabel name={name} kind={kind} value={value} />
        <div className="mt-xs">
          <ValueEditor kind={kind} value={value} path={path} />
        </div>
      </div>
    );
  }

  // Container (group / list): a titled section that nests its children.
  return (
    <div ref={ref} className={cn('rounded-md border-[0.5px] bg-canvas-paper', focusRing(highlighted))}>
      <div className="border-b-[0.5px] border-hairline bg-canvas-sand px-sm py-xs">
        <FieldLabel name={name} kind={kind} value={value} />
      </div>
      <div className="p-sm">
        <ValueEditor kind={kind} value={value} path={path} />
      </div>
    </div>
  );
}

/** An item inside a list. Positional (no key), so labelled by its 1-based index. */
function ItemRow({ index, value, path }: { index: number; value: Json; path: Path }) {
  const { t } = useTranslation();
  const kind = classify(value);
  const { ref, highlighted } = useFieldFocus(path);
  return (
    <div ref={ref} className={cn('rounded-md border-[0.5px] bg-canvas-paper', focusRing(highlighted))}>
      <div className="flex items-center gap-sm border-b-[0.5px] border-hairline bg-canvas-sand px-sm py-xs">
        <span className="font-mono text-label-sm uppercase tracking-[0.1em] text-ink-mute">
          {t('settings.pageCopy.itemLabel', { index: index + 1 })}
        </span>
        {kind === 'i18n' && <ArChip value={asI18n(value)} />}
      </div>
      <div className="p-sm">
        <ValueEditor kind={kind} value={value} path={path} />
      </div>
    </div>
  );
}

/** A field's human label + raw-key caption, with an AR-completeness chip for bilingual leaves. */
function FieldLabel({ name, kind, value }: { name: string; kind: NodeKind; value: Json }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-sm gap-y-3xs">
      <span className="text-small font-medium text-ink-deep">{humanizeKey(name)}</span>
      <span className="font-mono text-label-sm text-ink-mute">{name}</span>
      {kind === 'i18n' && (
        <span className="ms-auto">
          <ArChip value={asI18n(value)} />
        </span>
      )}
    </div>
  );
}

// ── Value editors by kind ─────────────────────────────────────────────────────
function ValueEditor({ kind, value, path }: { kind: NodeKind; value: Json; path: Path }) {
  const { update } = useTreeOps();
  const { t } = useTranslation();
  switch (kind) {
    case 'i18n':
      return (
        <I18nLeafEditor
          value={asI18n(value)}
          onChange={(v) => update(path, { en: v.en, ar: v.ar })}
        />
      );
    case 'group':
      return (
        <div className="border-s-[0.5px] border-hairline ps-sm">
          <GroupFields value={value as Record<string, Json>} path={path} />
        </div>
      );
    case 'list':
      return (
        <div className="border-s-[0.5px] border-hairline ps-sm">
          <ListFields value={value as Json[]} path={path} />
        </div>
      );
    case 'boolean':
      return (
        <Switch
          label={t('settings.pageCopy.booleanLabel')}
          checked={value as boolean}
          onCheckedChange={(v) => update(path, v)}
        />
      );
    case 'number':
      return (
        <input
          type="number"
          className={inputBase}
          value={Number.isFinite(value as number) ? (value as number) : ''}
          onChange={(e) => update(path, e.target.value === '' ? 0 : Number(e.target.value))}
        />
      );
    case 'null':
      return (
        <textarea
          dir="auto"
          placeholder={t('settings.pageCopy.emptyTextPlaceholder')}
          className={cn(inputBase, 'min-h-[64px] resize-y')}
          value=""
          onChange={(e) => update(path, e.target.value)}
        />
      );
    default:
      return (
        <textarea
          dir="auto"
          className={cn(inputBase, 'min-h-[64px] resize-y')}
          value={value as string}
          onChange={(e) => update(path, e.target.value)}
        />
      );
  }
}

// ── Bilingual leaf: EN + AR shown together (no tab toggling) ───────────────────
function I18nLeafEditor({ value, onChange }: { value: I18n; onChange: (v: I18n) => void }) {
  const { t } = useTranslation();
  return (
    <div className="grid gap-sm sm:grid-cols-2">
      <label className="flex flex-col gap-2xs">
        <span className="font-mono text-label-sm uppercase tracking-[0.1em] text-ink-mute">
          {t('common.en')}
        </span>
        <textarea
          dir="ltr"
          lang="en"
          value={value.en}
          onChange={(e) => onChange({ ...value, en: e.target.value })}
          className={cn(inputBase, 'min-h-[64px] resize-y')}
          aria-label={t('common.en')}
        />
      </label>
      <label className="flex flex-col gap-2xs">
        <span className="font-mono text-label-sm uppercase tracking-[0.1em] text-ink-mute">
          {t('common.ar')}
        </span>
        <textarea
          dir="rtl"
          lang="ar"
          value={value.ar}
          onChange={(e) => onChange({ ...value, ar: e.target.value })}
          className={cn(inputBase, 'min-h-[64px] resize-y font-arabic')}
          aria-label={t('common.ar')}
        />
      </label>
    </div>
  );
}

/** Compact EN/AR fill indicator for a bilingual leaf. */
function ArChip({ value }: { value: I18n }) {
  const en = value.en.trim();
  const ar = value.ar.trim();
  return (
    <span className="flex items-center gap-[6px] font-mono text-label-sm uppercase tracking-[0.1em]">
      <span className={en ? 'text-signal-teal' : 'text-ink-mute'}>EN{en ? ' ✓' : ' ·'}</span>
      <span className={ar ? 'text-signal-teal' : 'text-signal-amber-ink'}>AR{ar ? ' ✓' : ' ·'}</span>
    </span>
  );
}

/**
 * Root entry point — the content document is always an object at the top level. `focusField`
 * is the optional `field_path` to scroll to / highlight (set when arriving from search).
 */
export function PageCopyContentEditor({
  content,
  focusField = null,
  focusLocale = null,
}: {
  content: Json;
  focusField?: string | null;
  focusLocale?: 'en' | 'ar' | null;
}) {
  const { t } = useTranslation();
  if (!isPlainObject(content)) {
    return <p className="text-small text-signal-error">{t('settings.pageCopy.notObject')}</p>;
  }
  return (
    <FocusProvider path={focusField} locale={focusLocale}>
      <GroupFields value={content} path={[]} />
    </FocusProvider>
  );
}
